<script setup lang="ts">
import { computed, ref, watch, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';
import { TranslationService } from '@/services/translation.services';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  modelValue: boolean;
  group: {
    id: string;
    linkedProducts: { id: string; name: string }[];
    raw: any;
  } | null;
  // Tous les produits du commerce — sert de source pour le picker "attacher
  // à un produit" (exclut ceux déjà dans linkedProducts).
  allProducts: { id: string; designation: string }[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'saved'): void;
  (e: 'deleted'): void;
}>();

useBodyScrollLock(toRef(props, 'modelValue'));

function close() {
  emit('update:modelValue', false);
}

/* ── Traduction (backend interne) ────────────────────────────────────────── */
async function translateAll(
  src: 'fr' | 'en' | 'nl',
  get: () => string,
  set: (l: 'fr' | 'en' | 'nl', v: string) => void,
  busy: { value: boolean },
) {
  const text = get();
  if (!text.trim() || busy.value) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((l) => set(l, translated[l]));
  } finally {
    busy.value = false;
  }
}

/* ── État du formulaire du groupe ────────────────────────────────────────── */
const titleLang = ref<'fr' | 'en' | 'nl'>('fr');
const titleFr = ref('');
const titleEn = ref('');
const titleNl = ref('');
const titleTranslating = ref(false);
const choiceType = ref<'unique' | 'multiple'>('unique');
const required = ref(false);
const minQty = ref('0');
const maxQty = ref('1');
const exactQty = ref('');

const saving = ref(false);
const deleting = ref(false);
const error = ref<string | null>(null);

/* ── Options du groupe ───────────────────────────────────────────────────── */
interface OptionRow {
  id: string;
  contentFr: string;
  contentEn: string;
  contentNl: string;
  lang: 'fr' | 'en' | 'nl';
  price: string;
  translating: boolean;
  saving: boolean;
  deleting: boolean;
  isNew: boolean;
}
const options = ref<OptionRow[]>([]);

// Pré-remplissage auto des langues manquantes à partir de la première langue
// non vide trouvée, sans attendre un blur de l'utilisateur.
async function fillMissingTranslations(
  values: Record<'fr' | 'en' | 'nl', string>,
  setters: Record<'fr' | 'en' | 'nl', (v: string) => void>,
  busy: { value: boolean },
  stillCurrent: () => boolean,
) {
  const source = (['fr', 'en', 'nl'] as const).find((l) => values[l].trim());
  if (!source) return;
  const missing = (['fr', 'en', 'nl'] as const).filter(
    (l) => l !== source && !values[l].trim(),
  );
  if (!missing.length) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(
      values[source],
      source,
    );
    if (!stillCurrent()) return;
    missing.forEach((l) => setters[l](translated[l]));
  } finally {
    if (stillCurrent()) busy.value = false;
  }
}

async function resetFromGroup() {
  const raw = props.group?.raw ?? {};
  const thisGroupId = props.group?.id;
  titleLang.value = 'fr';
  const title = raw.title ?? {};
  titleFr.value = typeof title === 'string' ? title : (title.fr ?? '');
  titleEn.value = typeof title === 'string' ? '' : (title.en ?? '');
  titleNl.value = typeof title === 'string' ? '' : (title.nl ?? '');
  choiceType.value = raw.chooseType ?? raw.choiceType ?? 'unique';
  required.value = !!raw.required;
  minQty.value = String(raw.minQty ?? 0);
  maxQty.value = String(raw.maxQty ?? 1);
  exactQty.value = raw.exactQty != null ? String(raw.exactQty) : '';
  error.value = null;

  const rawOptions: any[] = raw.option ?? raw.options ?? [];
  options.value = rawOptions.map((o: any) => {
    const content = o.content ?? {};
    return {
      id: o.id ?? o._id ?? '',
      contentFr: typeof content === 'string' ? content : (content.fr ?? ''),
      contentEn: typeof content === 'string' ? '' : (content.en ?? ''),
      contentNl: typeof content === 'string' ? '' : (content.nl ?? ''),
      lang: 'fr' as const,
      price: o.price != null ? String(o.price) : '',
      translating: false,
      saving: false,
      deleting: false,
      isNew: false,
    };
  });

  const stillCurrent = () => props.group?.id === thisGroupId;
  await fillMissingTranslations(
    { fr: titleFr.value, en: titleEn.value, nl: titleNl.value },
    {
      fr: (v) => (titleFr.value = v),
      en: (v) => (titleEn.value = v),
      nl: (v) => (titleNl.value = v),
    },
    titleTranslating,
    stillCurrent,
  );
  await Promise.all(
    options.value.map((row) =>
      fillMissingTranslations(
        { fr: row.contentFr, en: row.contentEn, nl: row.contentNl },
        {
          fr: (v) => (row.contentFr = v),
          en: (v) => (row.contentEn = v),
          nl: (v) => (row.contentNl = v),
        },
        {
          get value() {
            return row.translating;
          },
          set value(x: boolean) {
            row.translating = x;
          },
        },
        stillCurrent,
      ),
    ),
  );
}

watch(
  () => props.group?.id,
  () => {
    if (props.modelValue) resetFromGroup();
  },
);
watch(
  () => props.modelValue,
  (open) => {
    if (open) resetFromGroup();
  },
);

function onTitleBlur(lang: 'fr' | 'en' | 'nl') {
  if (titleLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => titleFr.value,
    en: () => titleEn.value,
    nl: () => titleNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') titleFr.value = v;
      if (l === 'en') titleEn.value = v;
      if (l === 'nl') titleNl.value = v;
    },
    titleTranslating,
  );
}

async function saveGroup() {
  if (!props.group?.id || !titleFr.value.trim() || saving.value) return;
  saving.value = true;
  error.value = null;
  try {
    const body: Record<string, any> = {
      title: {
        fr: titleFr.value.trim(),
        en: titleEn.value.trim(),
        nl: titleNl.value.trim(),
      },
      choiceType: choiceType.value,
      required: required.value,
      minQty: parseInt(minQty.value) || 0,
      maxQty: parseInt(maxQty.value) || 1,
    };
    if (exactQty.value.trim()) body.exactQty = parseInt(exactQty.value);
    await axiosInstance.put(`extras/${props.group.id}`, body);
    emit('saved');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    saving.value = false;
  }
}

// ── Produits liés — attach/detach immédiats (extras/:groupId/products/:productId) ──
const detachingProductId = ref<string | null>(null);
const attachError = ref('');
const isAttachPickerOpen = ref(false);
const isAttachingProduct = ref(false);
const attachSearch = ref('');

const attachableProducts = computed(() => {
  const linkedIds = new Set(
    (props.group?.linkedProducts ?? []).map((p) => p.id),
  );
  const q = attachSearch.value.trim().toLowerCase();
  return props.allProducts
    .filter((p) => !linkedIds.has(p.id))
    .filter((p) => !q || p.designation.toLowerCase().includes(q));
});

function openAttachPicker() {
  attachSearch.value = '';
  attachError.value = '';
  isAttachPickerOpen.value = true;
}

async function attachProduct(productId: string) {
  if (!props.group?.id) return;
  isAttachingProduct.value = true;
  attachError.value = '';
  try {
    await axiosInstance.post(`extras/${props.group.id}/products/${productId}`);
    isAttachPickerOpen.value = false;
    emit('saved');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    attachError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? 'Impossible d’attacher ce produit');
  } finally {
    isAttachingProduct.value = false;
  }
}

async function detachProduct(productId: string) {
  if (!props.group?.id || detachingProductId.value) return;
  detachingProductId.value = productId;
  attachError.value = '';
  try {
    await axiosInstance.delete(
      `extras/${props.group.id}/products/${productId}`,
    );
    emit('saved');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    attachError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? 'Impossible de détacher ce produit');
  } finally {
    detachingProductId.value = null;
  }
}

const showDeleteConfirm = ref(false);

async function deleteGroup() {
  if (!props.group?.id || deleting.value) return;
  deleting.value = true;
  error.value = null;
  try {
    await axiosInstance.delete(`extras/${props.group.id}`);
    showDeleteConfirm.value = false;
    emit('deleted');
    close();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Impossible de supprimer ce groupe');
    showDeleteConfirm.value = false;
  } finally {
    deleting.value = false;
  }
}

/* ── Options — fonctions ─────────────────────────────────────────────────── */
function addOptionRow() {
  options.value.push({
    id: '',
    contentFr: '',
    contentEn: '',
    contentNl: '',
    lang: 'fr',
    price: '',
    translating: false,
    saving: false,
    deleting: false,
    isNew: true,
  });
}

function onOptionBlur(row: OptionRow, lang: 'fr' | 'en' | 'nl') {
  if (row.lang !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => row.contentFr,
    en: () => row.contentEn,
    nl: () => row.contentNl,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') row.contentFr = v;
      if (l === 'en') row.contentEn = v;
      if (l === 'nl') row.contentNl = v;
    },
    {
      get value() {
        return row.translating;
      },
      set value(x: boolean) {
        row.translating = x;
      },
    },
  );
}

async function saveOption(row: OptionRow) {
  if (!row.contentFr.trim() || row.saving) return;
  row.saving = true;
  try {
    const content = {
      fr: row.contentFr.trim(),
      en: row.contentEn.trim(),
      nl: row.contentNl.trim(),
    };
    if (row.isNew) {
      if (!props.group?.id) return;
      const res = await axiosInstance.post(`extra/${props.group.id}/options`, {
        type: 'custom',
        content,
        price: parseFloat(row.price) || 0,
      });
      const created = (res.data as any)?.data ?? (res.data as any);
      if (created?.id) {
        row.id = created.id;
        row.isNew = false;
      }
    } else {
      await axiosInstance.put(`addon/${row.id}`, { content });
    }
    emit('saved');
  } catch {
    /* silencieux, l'utilisateur reste sur le formulaire pour corriger */
  } finally {
    row.saving = false;
  }
}

async function removeOption(row: OptionRow) {
  if (row.deleting) return;
  if (!row.id) {
    options.value = options.value.filter((o) => o !== row);
    return;
  }
  row.deleting = true;
  try {
    await axiosInstance.delete(`addon/${row.id}`);
    options.value = options.value.filter((o) => o !== row);
    emit('saved');
  } catch {
    /* 409 potentiel si l'option ne peut pas être supprimée */
  } finally {
    row.deleting = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="cagd-backdrop">
      <div v-if="modelValue" class="cagd-backdrop" @click="close" />
    </Transition>
    <Transition name="cagd-drawer">
      <aside v-if="modelValue && group" class="cagd-drawer" @click.stop>
        <div class="cagd-inner">
          <div class="cagd-hd">
            <button class="cagd-back" @click="close">
              <i class="pi pi-times" />
              <span>{{ t('product.cancel') }}</span>
            </button>
          </div>

          <div class="cagd-title-block">
            <p class="cagd-eyebrow">{{ t('commerceDetail.editAddonGroup') }}</p>
            <h1 class="cagd-title">{{ titleFr || '—' }}</h1>
          </div>

          <!-- Produits liés — attach/detach immédiats -->
          <section class="cagd-section">
            <div class="cagd-section-hd">
              <span class="cagd-section-title">{{
                t('commerceDetail.colProdLinked')
              }}</span>
              <button class="cagd-add-btn" @click="openAttachPicker">
                <i class="pi pi-plus" /> {{ t('product.attachGroupAction') }}
              </button>
            </div>

            <p v-if="!group.linkedProducts.length" class="cagd-opts-empty">
              {{ t('commerceDetail.noLinkedProducts') }}
            </p>
            <div v-else class="cagd-linked">
              <span
                v-for="p in group.linkedProducts"
                :key="p.id"
                class="cagd-linked-chip"
              >
                {{ p.name }}
                <button
                  class="cagd-linked-chip-remove"
                  :disabled="detachingProductId === p.id"
                  :title="t('product.detachGroupTooltip')"
                  @click="detachProduct(p.id)"
                >
                  <i
                    :class="
                      detachingProductId === p.id
                        ? 'pi pi-spin pi-spinner'
                        : 'pi pi-times'
                    "
                  />
                </button>
              </span>
            </div>

            <p v-if="attachError" class="cagd-attach-error">
              {{ attachError }}
            </p>
          </section>

          <!-- Titre du groupe -->
          <section class="cagd-section">
            <div class="cagd-translabel-row">
              <label class="cagd-label">{{ t('product.inputLang') }}</label>
              <select v-model="titleLang" class="cagd-select cagd-select--sm">
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="nl">Nederlands</option>
              </select>
            </div>
            <div class="cagd-field-row">
              <div class="cagd-field">
                <label class="cagd-label">
                  <span
                    class="cagd-badge"
                    :class="{ 'cagd-badge--active': titleLang === 'fr' }"
                    >FR</span
                  >
                  <span class="cagd-req">*</span>
                </label>
                <input
                  v-model="titleFr"
                  class="cagd-input"
                  :class="{ 'cagd-input--active': titleLang === 'fr' }"
                  type="text"
                  @blur="onTitleBlur('fr')"
                />
              </div>
              <div class="cagd-field">
                <label class="cagd-label">
                  <span
                    class="cagd-badge"
                    :class="{ 'cagd-badge--active': titleLang === 'en' }"
                    >EN</span
                  >
                  <i
                    v-if="titleTranslating && titleLang !== 'en'"
                    class="pi pi-spin pi-spinner cagd-spin"
                  />
                </label>
                <input
                  v-model="titleEn"
                  class="cagd-input"
                  :class="{ 'cagd-input--active': titleLang === 'en' }"
                  type="text"
                  @blur="onTitleBlur('en')"
                />
              </div>
              <div class="cagd-field">
                <label class="cagd-label">
                  <span
                    class="cagd-badge"
                    :class="{ 'cagd-badge--active': titleLang === 'nl' }"
                    >NL</span
                  >
                  <i
                    v-if="titleTranslating && titleLang !== 'nl'"
                    class="pi pi-spin pi-spinner cagd-spin"
                  />
                </label>
                <input
                  v-model="titleNl"
                  class="cagd-input"
                  :class="{ 'cagd-input--active': titleLang === 'nl' }"
                  type="text"
                  @blur="onTitleBlur('nl')"
                />
              </div>
            </div>
          </section>

          <!-- Paramètres du groupe -->
          <section class="cagd-section">
            <div class="cagd-field-row">
              <div class="cagd-field">
                <label class="cagd-label">{{
                  t('commerceDetail.colChoice')
                }}</label>
                <select v-model="choiceType" class="cagd-select">
                  <option value="unique">
                    {{ t('commerceDetail.unique') }}
                  </option>
                  <option value="multiple">
                    {{ t('commerceDetail.multiple') }}
                  </option>
                </select>
              </div>
              <div class="cagd-field cagd-field--toggle">
                <label class="cagd-label">{{
                  t('commerceDetail.required')
                }}</label>
                <button
                  type="button"
                  class="cagd-toggle"
                  :class="{ 'cagd-toggle--on': required }"
                  @click="required = !required"
                >
                  <span class="cagd-toggle-dot" />
                </button>
              </div>
            </div>
            <div class="cagd-field-row">
              <div class="cagd-field">
                <label class="cagd-label">Min</label>
                <input
                  v-model="minQty"
                  class="cagd-input"
                  type="number"
                  min="0"
                />
              </div>
              <div class="cagd-field">
                <label class="cagd-label">Max</label>
                <input
                  v-model="maxQty"
                  class="cagd-input"
                  type="number"
                  min="0"
                />
              </div>
              <div class="cagd-field">
                <label class="cagd-label">Exact</label>
                <input
                  v-model="exactQty"
                  class="cagd-input"
                  type="number"
                  min="0"
                  placeholder="—"
                />
              </div>
            </div>
          </section>

          <div v-if="error" class="cagd-error">
            <i class="pi pi-exclamation-circle" /> {{ error }}
          </div>

          <div class="cagd-actions">
            <button
              class="cagd-btn cagd-btn--danger"
              :disabled="deleting"
              @click="showDeleteConfirm = true"
            >
              <i v-if="deleting" class="pi pi-spin pi-spinner" />
              <i v-else class="pi pi-trash" />
              {{ t('product.delete') }}
            </button>
            <button
              class="cagd-btn cagd-btn--primary"
              :disabled="saving || !titleFr.trim()"
              @click="saveGroup()"
            >
              <i v-if="saving" class="pi pi-spin pi-spinner" />
              <i v-else class="pi pi-check" />
              {{ saving ? t('product.saving') : t('product.save') }}
            </button>
          </div>

          <!-- Options -->
          <section class="cagd-section">
            <div class="cagd-section-hd">
              <span class="cagd-section-title">{{
                t('commerceDetail.colOptions')
              }}</span>
              <button class="cagd-add-btn" @click="addOptionRow()">
                <i class="pi pi-plus" /> {{ t('product.add') }}
              </button>
            </div>

            <div v-if="!options.length" class="cagd-opts-empty">
              {{ t('commerceDetail.emptyOptions') }}
            </div>

            <div
              v-for="row in options"
              :key="row.id || Math.random()"
              class="cagd-opt-card"
            >
              <div class="cagd-opt-hd">
                <select v-model="row.lang" class="cagd-select cagd-select--sm">
                  <option value="fr">FR</option>
                  <option value="en">EN</option>
                  <option value="nl">NL</option>
                </select>
                <i
                  v-if="row.translating"
                  class="pi pi-spin pi-spinner cagd-spin"
                />
                <button
                  class="cagd-icon-btn cagd-icon-btn--danger"
                  style="margin-left: auto"
                  :disabled="row.deleting"
                  @click="removeOption(row)"
                >
                  <i v-if="row.deleting" class="pi pi-spin pi-spinner" />
                  <i v-else class="pi pi-trash" />
                </button>
              </div>
              <div class="cagd-field-row">
                <div class="cagd-field">
                  <label class="cagd-label">
                    <span
                      class="cagd-badge"
                      :class="{ 'cagd-badge--active': row.lang === 'fr' }"
                      >FR</span
                    >
                  </label>
                  <input
                    v-model="row.contentFr"
                    class="cagd-input"
                    :class="{ 'cagd-input--active': row.lang === 'fr' }"
                    type="text"
                    @blur="onOptionBlur(row, 'fr')"
                  />
                </div>
                <div class="cagd-field">
                  <label class="cagd-label">
                    <span
                      class="cagd-badge"
                      :class="{ 'cagd-badge--active': row.lang === 'en' }"
                      >EN</span
                    >
                  </label>
                  <input
                    v-model="row.contentEn"
                    class="cagd-input"
                    :class="{ 'cagd-input--active': row.lang === 'en' }"
                    type="text"
                    @blur="onOptionBlur(row, 'en')"
                  />
                </div>
                <div class="cagd-field">
                  <label class="cagd-label">
                    <span
                      class="cagd-badge"
                      :class="{ 'cagd-badge--active': row.lang === 'nl' }"
                      >NL</span
                    >
                  </label>
                  <input
                    v-model="row.contentNl"
                    class="cagd-input"
                    :class="{ 'cagd-input--active': row.lang === 'nl' }"
                    type="text"
                    @blur="onOptionBlur(row, 'nl')"
                  />
                </div>
                <div class="cagd-field" style="max-width: 7rem">
                  <label class="cagd-label">{{
                    t('commerceDetail.colPrice')
                  }}</label>
                  <input
                    v-model="row.price"
                    class="cagd-input"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div class="cagd-opt-actions">
                <button
                  class="cagd-btn cagd-btn--primary cagd-btn--sm"
                  :disabled="row.saving || !row.contentFr.trim()"
                  @click="saveOption(row)"
                >
                  <i v-if="row.saving" class="pi pi-spin pi-spinner" />
                  <i v-else class="pi pi-check" />
                  {{ row.saving ? t('product.saving') : t('product.save') }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </Transition>
  </Teleport>

  <!-- Modale confirmation suppression -->
  <Teleport to="body">
    <div
      v-if="showDeleteConfirm"
      class="cagd-modal-backdrop"
      @click.self="showDeleteConfirm = false"
    >
      <div class="cagd-modal">
        <div class="cagd-modal-ico"><i class="pi pi-trash" /></div>
        <h2 class="cagd-modal-title">
          {{ t('product.deleteGroupConfirmTitle') }}
        </h2>
        <p class="cagd-modal-text">
          {{ t('product.deleteGroupConfirmBody') }}
        </p>
        <div class="cagd-modal-actions">
          <button
            class="cagd-btn cagd-btn--ghost"
            :disabled="deleting"
            @click="showDeleteConfirm = false"
          >
            {{ t('product.cancel') }}
          </button>
          <button
            class="cagd-btn cagd-btn--danger"
            :disabled="deleting"
            @click="deleteGroup()"
          >
            <i v-if="deleting" class="pi pi-spin pi-spinner" />
            <i v-else class="pi pi-trash" />
            {{ deleting ? t('product.deleting') : t('product.delete') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Picker "attacher à un produit" -->
  <Teleport to="body">
    <div
      v-if="isAttachPickerOpen"
      class="cagd-modal-backdrop"
      @click.self="isAttachPickerOpen = false"
    >
      <div class="cagd-modal cagd-modal--wide">
        <div class="cagd-attach-hd">
          <h2 class="cagd-modal-title">{{ t('product.attachGroupTitle') }}</h2>
          <button class="cagd-icon-btn" @click="isAttachPickerOpen = false">
            <i class="pi pi-times" />
          </button>
        </div>
        <input
          v-model="attachSearch"
          class="cagd-input"
          type="text"
          :placeholder="t('commerceDetail.searchProduct')"
          style="width: 100%; margin-bottom: 0.875rem"
        />
        <p v-if="attachError" class="cagd-attach-error">{{ attachError }}</p>
        <div class="cagd-attach-list">
          <p v-if="!attachableProducts.length" class="cagd-opts-empty">
            {{ t('product.attachGroupEmpty') }}
          </p>
          <button
            v-for="p in attachableProducts"
            :key="p.id"
            class="cagd-attach-item"
            :disabled="isAttachingProduct"
            @click="attachProduct(p.id)"
          >
            <span>{{ p.designation }}</span>
            <i class="pi pi-link" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cagd-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(8, 12, 20, 0.45);
  backdrop-filter: blur(2px);
}
.cagd-backdrop-enter-active,
.cagd-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.cagd-backdrop-enter-from,
.cagd-backdrop-leave-to {
  opacity: 0;
}
.cagd-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: min(560px, 100vw);
  background: #f4f6f9;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
[data-theme='dark'] .cagd-drawer {
  background: #0c1421;
}
.cagd-drawer-enter-active,
.cagd-drawer-leave-active {
  transition: transform 0.25s ease;
}
.cagd-drawer-enter-from,
.cagd-drawer-leave-to {
  transform: translateX(100%);
}
.cagd-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem;
  box-sizing: border-box;
  overflow-y: auto;
}
.cagd-hd {
  display: flex;
  align-items: center;
}
.cagd-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-primary, #f68402);
  padding: 0;
}
.cagd-title-block {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cagd-eyebrow {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-primary, #f68402);
  margin: 0;
  opacity: 0.8;
}
.cagd-title {
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface, #111);
  margin: 0;
  letter-spacing: -0.02em;
}
[data-theme='dark'] .cagd-title {
  color: #f1f5f9;
}
.cagd-linked {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}
.cagd-linked-label {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.cagd-linked-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.375rem 0.2rem 0.5rem;
  border-radius: 0.375rem;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .cagd-linked-chip {
  background: rgba(255, 255, 255, 0.08);
}
.cagd-linked-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.08);
  color: inherit;
  font-size: 0.625rem;
  cursor: pointer;
}
.cagd-linked-chip-remove:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}
.cagd-linked-chip-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cagd-attach-error {
  font-size: 0.75rem;
  color: #ef4444;
  margin: 0;
}
.cagd-modal--wide {
  max-width: 28rem;
  text-align: left;
}
.cagd-attach-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.cagd-attach-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 18rem;
  overflow-y: auto;
}
.cagd-attach-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: transparent;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  cursor: pointer;
  transition: background 0.15s;
}
.cagd-attach-item:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}
.cagd-attach-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
[data-theme='dark'] .cagd-attach-item {
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cagd-attach-item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}

.cagd-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.125rem;
  border-radius: 0.875rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
}
[data-theme='dark'] .cagd-section {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
.cagd-section-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cagd-section-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.cagd-translabel-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cagd-field-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.cagd-field {
  flex: 1;
  min-width: 6rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.cagd-field--toggle {
  justify-content: flex-end;
}
.cagd-label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.cagd-req {
  color: #ef4444;
}
.cagd-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.125rem;
  padding: 0 0.3rem;
  border-radius: 0.3125rem;
  font-size: 0.5625rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .cagd-badge {
  background: rgba(255, 255, 255, 0.08);
}
.cagd-badge--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .cagd-badge--active {
  background: #fff;
  color: #0b1320;
}
.cagd-spin {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-variant);
}
.cagd-input,
.cagd-select {
  height: 2.25rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface, #171310);
  outline: none;
  transition: border-color 0.15s;
}
[data-theme='dark'] .cagd-input,
[data-theme='dark'] .cagd-select {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f2ee;
}
.cagd-input:focus,
.cagd-select:focus {
  border-color: #0b1320;
}
[data-theme='dark'] .cagd-input:focus,
[data-theme='dark'] .cagd-select:focus {
  border-color: #fff;
}
.cagd-input--active {
  border-color: rgba(11, 19, 32, 0.4);
}
.cagd-select--sm {
  height: 1.875rem;
  font-size: 0.75rem;
}

.cagd-toggle {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  border: none;
  background: rgba(0, 0, 0, 0.15);
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}
.cagd-toggle--on {
  background: #0b1320;
}
[data-theme='dark'] .cagd-toggle--on {
  background: #fff;
}
.cagd-toggle-dot {
  position: absolute;
  top: 0.15rem;
  left: 0.15rem;
  width: 1.0625rem;
  height: 1.0625rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}
[data-theme='dark'] .cagd-toggle-dot {
  background: #0b1320;
}
.cagd-toggle--on .cagd-toggle-dot {
  transform: translateX(1.125rem);
}

.cagd-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  font-size: 0.8125rem;
}
.cagd-actions {
  display: flex;
  justify-content: space-between;
  gap: 0.625rem;
}
.cagd-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5625rem 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.cagd-btn--sm {
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
}
.cagd-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cagd-btn--primary {
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
}
.cagd-btn--danger {
  background: transparent;
  border: 1.5px solid rgba(239, 68, 68, 0.35);
  color: #ef4444;
}
.cagd-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.08);
}
.cagd-btn--ghost {
  background: transparent;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  color: var(--nxt-on-surface);
}
.cagd-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .cagd-btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}

/* Modale confirmation suppression */
.cagd-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.cagd-modal {
  background: var(--nxt-card-bg, #fff);
  border-radius: 1rem;
  padding: 1.75rem;
  max-width: 24rem;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
[data-theme='dark'] .cagd-modal {
  background: #1e2025;
}
.cagd-modal-ico {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}
.cagd-modal-title {
  margin: 0 0 0.5rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cagd-modal-text {
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant, #666);
  line-height: 1.5;
}
.cagd-modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
}

.cagd-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: none;
  background: none;
  color: var(--nxt-primary, #f68402);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}
.cagd-opts-empty {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  text-align: center;
  padding: 1rem 0;
}
.cagd-opt-card {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.875rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .cagd-opt-card {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
.cagd-opt-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cagd-opt-actions {
  display: flex;
  justify-content: flex-end;
}
.cagd-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
[data-theme='dark'] .cagd-icon-btn {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.cagd-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cagd-icon-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}
</style>
